import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE } from '../../../../ApiUrl';
import {
  COUNTRY_OPTIONS,
  createEmptyVisa,
  createEmptyCDC,
  createEmptyCoC,
  createEmptyEducation,
  createEmptyCertification,
  createEmptyTraining,
} from '../constants';

// Combined endpoint for creating multiple records in one request.
const CREDENTIAL_ENDPOINT = `${API_BASE}/combined-credential-record/`;
// Per-record endpoint for updates and deletes of an existing CredentialRecord.
const CREDENTIAL_RECORD_URL = `${API_BASE}/credential-records/`;

export const useCredentialRecords = (professionalProfileId, tenantId) => {
  const [travelDocuments, setTravelDocuments] = useState({
    passport_number: '',
    passport_country: '',
    passport_expiry: '',
    visas: [createEmptyVisa()],
    _backendId: null,
  });

  const [marineDocuments, setMarineDocuments] = useState({
    cdc: [createEmptyCDC()],
    coc: [createEmptyCoC()],
  });

  const [educationRecords, setEducationRecords] = useState([createEmptyEducation()]);
  const [certificationRecords, setCertificationRecords] = useState([createEmptyCertification()]);
  const [trainingRecords, setTrainingRecords] = useState([createEmptyTraining()]);

  // ----- Hydration helpers -----
  const CERTIFICATION_STATUS_MAP_REVERSE = {
    ACTIVE: 'Active',
    EXPIRED: 'Expired',
    REVOKED: 'Suspended',
  };
  const MEMBERSHIP_STATUS_MAP_REVERSE = {
    ACTIVE: 'Active',
    EXPIRED: 'Lapsed',
    ARCHIVED: 'Honorary',
    DRAFT: 'Pending',
    REVOKED: 'Lapsed',
  };

  // Helper function to extract backend ID from various formats
  const extractBackendId = (recordId, prefix) => {
    if (!recordId) return null;
    
    // If it's already a number, return it
    if (/^\d+$/.test(recordId)) {
      return parseInt(recordId);
    }
    
    // Handle pattern like "visa-123" or "cdc-456"
    const match = String(recordId).match(/^([a-zA-Z]+)-(\d+)$/);
    if (match) {
      return parseInt(match[2]);
    }
    
    return null;
  };

  const hydrateFromCredentials = (records) => {
    if (!Array.isArray(records) || records.length === 0) return;

    const byType = {};
    records.forEach((rec) => {
      if (!byType[rec.record_type]) byType[rec.record_type] = [];
      byType[rec.record_type].push(rec);
    });

    // Passport
    const passportRecord = (byType.PASSPORT || [])[0];
    if (passportRecord) {
      setTravelDocuments((prev) => ({
        ...prev,
        _backendId: passportRecord.id,
        passport_number: passportRecord.credential_number || '',
        passport_country: passportRecord.issuing_country_code || '',
        passport_expiry: passportRecord.expiry_date || '',
        visas: Array.isArray(passportRecord.items) && passportRecord.items.length > 0
          ? passportRecord.items.map((item) => ({
              id: `visa-${item.id}`,
              _backendId: item.id,
              country: item.country_code || '',
              visa_type: item.title || '',
              visa_number: item.item_number || '',
              issue_date: item.issue_date || '',
              expiry_date: item.expiry_date || '',
              errors: {},
            }))
          : [createEmptyVisa()],
      }));
    }

    // Marine CDC
    const cdcRecords = byType.MARINE_CDC || [];
    if (cdcRecords.length > 0) {
      setMarineDocuments((prev) => ({
        ...prev,
        cdc: cdcRecords.map((rec) => ({
          id: `cdc-${rec.id}`,
          _backendId: rec.id,
          number: rec.credential_number || '',
          issuing_country: rec.issuing_country_code || '',
          issue_date: rec.issue_date || '',
          expiry_date: rec.expiry_date || '',
          errors: {},
        })),
      }));
    }

    // Marine CoC
    const cocRecords = byType.MARINE_COC || [];
    if (cocRecords.length > 0) {
      setMarineDocuments((prev) => ({
        ...prev,
        coc: cocRecords.map((rec) => ({
          id: `coc-${rec.id}`,
          _backendId: rec.id,
          number: rec.credential_number || '',
          grade: rec.level_or_grade || '',
          issuing_authority: rec.issuing_body_snapshot || '',
          issue_date: rec.issue_date || '',
          expiry_date: rec.expiry_date || '',
          endorsements: rec.restrictions_or_limitations || '',
          errors: {},
        })),
      }));
    }

    // Education
    const eduRecords = byType.EDUCATION || [];
    if (eduRecords.length > 0) {
      setEducationRecords(
        eduRecords.map((rec) => ({
          id: `edu-${rec.id}`,
          _backendId: rec.id,
          qualification: rec.title || '',
          discipline: rec.discipline_or_field || '',
          institute: rec.issuing_body_snapshot || '',
          country: rec.issuing_country_code || '',
          start_date: rec.start_date || '',
          end_date: rec.end_date || '',
          grade: rec.level_or_grade || '',
          certificate_file: null,
          file_name: '',
          errors: {},
        }))
      );
    }

    // Certifications
    const certRecords = byType.CERTIFICATION || [];
    if (certRecords.length > 0) {
      setCertificationRecords(
        certRecords.map((rec) => ({
          id: `cert-${rec.id}`,
          _backendId: rec.id,
          body: rec.issuing_body_snapshot || '',
          certification: rec.title || '',
          level: rec.level_or_grade || '',
          number: rec.credential_number || '',
          issue_date: rec.issue_date || '',
          expiry_date: rec.expiry_date || '',
          status: CERTIFICATION_STATUS_MAP_REVERSE[rec.status] || 'Active',
          linked_scope: rec.details?.linked_scope || '',
          certificate_file: null,
          file_name: '',
          errors: {},
        }))
      );
    }

    // Training
    const trainingRecordsApi = byType.TRAINING || [];
    if (trainingRecordsApi.length > 0) {
      setTrainingRecords(
        trainingRecordsApi.map((rec) => ({
          id: `training-${rec.id}`,
          _backendId: rec.id,
          course_name: rec.title || '',
          provider: rec.issuing_body_snapshot || '',
          start_date: rec.start_date || '',
          end_date: rec.end_date || '',
          expiry_date: rec.expiry_date || '',
          certificate_file: null,
          file_name: '',
          errors: {},
        }))
      );
    }
  };

  // NEW: Hydrate from related records API response
  const hydrateFromRelatedRecords = (data) => {
    if (!data) return;
    
    // Hydrate credentials if present
    if (data.credentials && Array.isArray(data.credentials)) {
      hydrateFromCredentials(data.credentials);
    }
  };

  // Fetch on mount when professionalProfileId changes - UPDATED to use related-records endpoint
  useEffect(() => {
    if (!professionalProfileId) return;
    const loadCredentials = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/professional-profiles/${professionalProfileId}/related-records/`
        );
        // The response structure might be { data: { credentials: [...] } }
        const responseData = res?.data?.data || res?.data;
        hydrateFromRelatedRecords(responseData);
      } catch (err) {
        console.error('Failed to load credential records', err);
      }
    };
    loadCredentials();
  }, [professionalProfileId]);

  // ----- Build payload for Tab 2 -----
  const countryLabel = (code) =>
    COUNTRY_OPTIONS.find((opt) => opt.value === code)?.label || '';

  const buildPayload = () => {
    const records = [];

    const activeVisas = travelDocuments.visas.filter(
      (visa) => visa.country || visa.visa_type?.trim() || visa.visa_number?.trim()
    );
    
    const hasPassport =
      travelDocuments.passport_number?.trim() ||
      travelDocuments.passport_country ||
      travelDocuments.passport_expiry;

    // Passport with Visa Items
    if (hasPassport || activeVisas.length > 0) {
      const passportBackendId = travelDocuments._backendId || null;
      
      // Build visa items with proper backend IDs
      const visaItems = activeVisas.map((visa) => {
        const visaBackendId = visa._backendId || null;
        
        return {
          _backendId: visaBackendId,
          item_type: 'VISA_WORK_PERMIT',
          tenant: tenantId || '',
          title: visa.visa_type || '',
          item_number: visa.visa_number || '',
          country_code: visa.country || '',
          issue_date: visa.issue_date || null,
          expiry_date: visa.expiry_date || null,
          status: 'ACTIVE',
          // Include credential_record reference for existing items
          ...(visaBackendId && { credential_record: passportBackendId }),
        };
      });

      records.push({
        _backendId: passportBackendId,
        professional: professionalProfileId,
        tenant: tenantId || '',
        record_type: 'PASSPORT',
        title: 'Passport',
        issuing_body_snapshot: countryLabel(travelDocuments.passport_country) || 'Passport Authority',
        issuing_country_code: travelDocuments.passport_country || '',
        credential_number: travelDocuments.passport_number || '',
        expiry_date: travelDocuments.passport_expiry || null,
        status: 'ACTIVE',
        items: visaItems,
      });
    }

    // Marine CDC
    (marineDocuments.cdc || [])
      .filter((item) => item.number?.trim() || item.issuing_country)
      .forEach((item) => {
        records.push({
          _backendId: extractBackendId(item.id, 'cdc'),
          professional: professionalProfileId,
          tenant: tenantId || '',
          record_type: 'MARINE_CDC',
          title: 'CDC / Seaman Book',
          issuing_body_snapshot: countryLabel(item.issuing_country) || 'Maritime Authority',
          issuing_country_code: item.issuing_country || '',
          credential_number: item.number || '',
          issue_date: item.issue_date || null,
          expiry_date: item.expiry_date || null,
          status: 'ACTIVE',
        });
      });

    // Marine CoC
    (marineDocuments.coc || [])
      .filter((item) => item.number?.trim() || item.issuing_authority?.trim())
      .forEach((item) => {
        records.push({
          _backendId: extractBackendId(item.id, 'coc'),
          professional: professionalProfileId,
          tenant: tenantId || '',
          record_type: 'MARINE_COC',
          title: 'Certificate of Competency',
          issuing_body_snapshot: item.issuing_authority?.trim() || 'Maritime Authority',
          level_or_grade: item.grade || '',
          credential_number: item.number || '',
          issue_date: item.issue_date || null,
          expiry_date: item.expiry_date || null,
          restrictions_or_limitations: item.endorsements || '',
          status: 'ACTIVE',
        });
      });

    // Education
    educationRecords
      .filter((rec) => rec.qualification?.trim() || rec.institute?.trim())
      .forEach((rec) => {
        records.push({
          _backendId: extractBackendId(rec.id, 'edu'),
          professional: professionalProfileId,
          tenant: tenantId || '',
          record_type: 'EDUCATION',
          title: rec.qualification || '',
          discipline_or_field: rec.discipline || '',
          issuing_body_snapshot: rec.institute?.trim() || 'Not specified',
          issuing_country_code: rec.country || '',
          level_or_grade: rec.grade || '',
          start_date: rec.start_date || null,
          end_date: rec.end_date || null,
          status: 'ACTIVE',
        });
      });

    // Certifications
    const CERTIFICATION_STATUS_MAP = {
      Active: 'ACTIVE',
      Expired: 'EXPIRED',
      Suspended: 'REVOKED',
    };
    certificationRecords
      .filter((rec) => rec.certification?.trim() || rec.body?.trim())
      .forEach((rec) => {
        records.push({
          _backendId: extractBackendId(rec.id, 'cert'),
          professional: professionalProfileId,
          tenant: tenantId || '',
          record_type: 'CERTIFICATION',
          title: rec.certification || '',
          issuing_body_snapshot: rec.body?.trim() || 'Not specified',
          level_or_grade: rec.level || '',
          credential_number: rec.number || '',
          issue_date: rec.issue_date || null,
          expiry_date: rec.expiry_date || null,
          status: CERTIFICATION_STATUS_MAP[rec.status] || 'ACTIVE',
          details: rec.linked_scope?.trim() ? { linked_scope: rec.linked_scope } : {},
        });
      });

    // Training
    trainingRecords
      .filter((rec) => rec.course_name?.trim() || rec.provider?.trim())
      .forEach((rec) => {
        records.push({
          _backendId: extractBackendId(rec.id, 'training'),
          professional: professionalProfileId,
          tenant: tenantId || '',
          record_type: 'TRAINING',
          title: rec.course_name || '',
          issuing_body_snapshot: rec.provider?.trim() || 'Not specified',
          start_date: rec.start_date || null,
          end_date: rec.end_date || null,
          expiry_date: rec.expiry_date || null,
          status: 'ACTIVE',
        });
      });

    return records;
  };

  // ----- Validation -----
  const validate = () => {
    const errors = {};
    const hasEducation = educationRecords.some((rec) => rec.qualification?.trim());
    if (!hasEducation) {
      errors.education = 'At least one education record with qualification is required';
    }
    const hasCert = certificationRecords.some((rec) => rec.certification?.trim() || rec.body?.trim());
    if (!hasCert) {
      errors.certification = 'At least one certification record is required';
    }
    return errors;
  };

  // ----- Submit -----
  const submit = async () => {
    if (!professionalProfileId) {
      throw new Error('Professional profile ID missing.');
    }
    const allRecords = buildPayload();

    const toCreate = allRecords
      .filter((rec) => !rec._backendId)
      .map(({ _backendId, ...rest }) => rest);

    const toUpdate = allRecords.filter((rec) => rec._backendId);

    try {
      if (toCreate.length > 0) {
        await axios.post(CREDENTIAL_ENDPOINT, toCreate);
      }

      if (toUpdate.length > 0) {
        await Promise.all(
          toUpdate.map(({ _backendId, ...rest }) =>
            axios.put(`${CREDENTIAL_RECORD_URL}${_backendId}/`, rest)
          )
        );
      }
      
      // Show success message
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Credentials saved successfully!',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Submit error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to save credentials. Please try again.',
      });
      throw error;
    }
  };

  // ----- Record management helpers (add/remove/change) -----
  const addRecord = (setter, creator) => {
    setter((prev) => [...prev, creator()]);
  };

  const removeRecord = (setter, index, creator) => {
    setter((prev) => {
      if (prev.length === 1) return [creator()];
      return prev.filter((_, i) => i !== index);
    });
  };

  // Fires DELETE against the backend for a single existing CredentialRecord.
  const deleteCredentialRecordFromBackend = (backendId) => {
    if (!backendId) return;
    axios.delete(`${CREDENTIAL_RECORD_URL}${backendId}/`).catch((err) => {
      console.error('Failed to delete credential record from backend', err);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: "Couldn't delete this record from the server. Please try again.",
      });
    });
  };

  // ----- Visa specific functions -----
  const addVisa = () => {
    setTravelDocuments((prev) => ({
      ...prev,
      visas: [...prev.visas, { ...createEmptyVisa(), id: `visa-${Date.now()}` }]
    }));
  };

  const removeVisa = (index) => {
    setTravelDocuments((prev) => {
      const visaToRemove = prev.visas[index];
      
      // If this visa has a backend ID, delete it from the server
      if (visaToRemove._backendId) {
        // Note: You might need to delete via the parent or a specific endpoint
        // This assumes the parent record will be updated to remove the item
        deleteCredentialItemFromBackend(visaToRemove._backendId);
      }
      
      const newVisas = prev.visas.filter((_, i) => i !== index);
      // Keep at least one empty visa
      if (newVisas.length === 0) {
        return { ...prev, visas: [{ ...createEmptyVisa(), id: `visa-${Date.now()}` }] };
      }
      return { ...prev, visas: newVisas };
    });
  };

  const handleVisaChange = (index, field, value) => {
    setTravelDocuments((prev) => {
      const updatedVisas = [...prev.visas];
      updatedVisas[index] = { 
        ...updatedVisas[index], 
        [field]: value 
      };
      return { ...prev, visas: updatedVisas };
    });
  };

  // Function to delete individual credential items
  const deleteCredentialItemFromBackend = async (itemId) => {
    if (!itemId) return;
    try {
      // This might need to be adjusted based on your API
      // Option 1: Delete via item endpoint
      await axios.delete(`${API_BASE}/credential-items/${itemId}/`);
      
      // Option 2: If items are deleted through the parent, 
      // you might need to update the parent record instead
      // The parent record will be updated on the next submit
    } catch (err) {
      console.error('Failed to delete credential item from backend', err);
      // Don't show error to user as the parent record will handle it
    }
  };

  // Same behaviour as removeRecord, but for arrays hydrated from the
  // credential-records API
  const removeEducationRecord = (index) => {
    const backendId = educationRecords[index]?._backendId;
    removeRecord(setEducationRecords, index, createEmptyEducation);
    deleteCredentialRecordFromBackend(backendId);
  };

  const removeCertificationRecord = (index) => {
    const backendId = certificationRecords[index]?._backendId;
    removeRecord(setCertificationRecords, index, createEmptyCertification);
    deleteCredentialRecordFromBackend(backendId);
  };

  const removeTrainingRecord = (index) => {
    const backendId = trainingRecords[index]?._backendId;
    removeRecord(setTrainingRecords, index, createEmptyTraining);
    deleteCredentialRecordFromBackend(backendId);
  };

  const removeCdcRecord = (index) => {
    const backendId = marineDocuments.cdc[index]?._backendId;
    setMarineDocuments((prev) => {
      if (prev.cdc.length === 1) return { ...prev, cdc: [createEmptyCDC()] };
      return { ...prev, cdc: prev.cdc.filter((_, i) => i !== index) };
    });
    deleteCredentialRecordFromBackend(backendId);
  };

  const removeCocRecord = (index) => {
    const backendId = marineDocuments.coc[index]?._backendId;
    setMarineDocuments((prev) => {
      if (prev.coc.length === 1) return { ...prev, coc: [createEmptyCoC()] };
      return { ...prev, coc: prev.coc.filter((_, i) => i !== index) };
    });
    deleteCredentialRecordFromBackend(backendId);
  };

  const handleRecordChange = (setter, index, field, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRecordFileChange = (setter, index, file, fileField = 'certificate_file') => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [fileField]: file,
        file_name: file ? file.name : '',
      };
      return updated;
    });
  };

  // Passport field change handler
  const handlePassportChange = (field, value) => {
    setTravelDocuments((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  return {
    // State
    travelDocuments,
    marineDocuments,
    educationRecords,
    certificationRecords,
    trainingRecords,
    
    // Setters
    setTravelDocuments,
    setMarineDocuments,
    setEducationRecords,
    setCertificationRecords,
    setTrainingRecords,
    
    // Hydration
    hydrateFromCredentials,
    hydrateFromRelatedRecords,
    
    // Validation
    validate,
    
    // Submit
    submit,
    
    // Record helpers
    addRecord,
    removeRecord,
    handleRecordChange,
    handleRecordFileChange,
    removeEducationRecord,
    removeCertificationRecord,
    removeTrainingRecord,
    removeCdcRecord,
    removeCocRecord,
    
    // Visa specific functions
    addVisa,
    removeVisa,
    handleVisaChange,
    
    // Passport change handler
    handlePassportChange,
  };
};